import { useRef } from 'react';
import { useLocation } from 'react-router';
import { professionalTabBase } from '@modules/professional/util/professional-tab-base';

import { RoccoLink } from '@rocco/components/link';
import { cn } from '@rocco/utils/cn';
import { useFeature } from '~/modules/root/hook/use-feature';
import { useEditPermissions } from '~/modules/user';
import { getSubRoutes } from '@modules/professional/component/sub-route-navigation/utils';
import type { ProfessionalInitialResult } from '@modules/professional/hook/use-professional-initial-route-data';
import { getActiveTabLink } from '~/ui-rocco/pages/professional-detail/utils/subnav-active';
import { useTabScrollAnchor } from '~/ui-rocco/pages/professional-detail/hooks/use-tab-scroll-anchor';
import { buildProfessionalNavItems } from '~/ui-rocco/pages/professional-detail/utils/professional-nav-items';
import { ProfessionalContactTabButton } from './ProfessionalContactTabButton';
import { useProfessionalLayoutData } from '@modules/professional/hook/use-professional-layout-data';

/** Match the Figma's tab labels (legacy uses Profile). */
const LABEL_OVERRIDES: Partial<Record<string, string>> = {
    Profile: 'Overview',
};

/**
 * Tab bar under the profile banner's logo — the professional's available tabs
 * (Overview / About / Projects / Products / Articles / Network / Contact …) as
 * a single row of the directory pill bar's pills that scrolls horizontally,
 * the active tab the filled one, with the bar's rule under it. Reuses
 * {@link getSubRoutes} so only real tabs show. Lives in the persistent layout
 * so it renders once and never re-flashes on tab nav.
 */
export const ProfessionalSubNav = () => {
    const layoutData = useProfessionalLayoutData();
    const { pathname } = useLocation();
    const imageLabelSearchEnabled = useFeature('image_label_search_profile');
    const initial = layoutData?.initial;
    // Editors with Products allowed but no products yet still need the Products
    // tab to add their first product — mirror the legacy nav's edit permission.
    const { canEditProducts } = useEditPermissions(initial?.ID ?? 0);

    // Switching tabs leaves the scroll position alone — see useTabScrollAnchor.
    const navRef = useRef<HTMLElement>(null);
    useTabScrollAnchor(navRef);

    if (!initial) return null;

    const currentPath = pathname.replace(/\/+$/, '');
    const base = professionalTabBase(initial.URLSegment, currentPath);
    const professional = initial as unknown as ProfessionalInitialResult;
    // Match prod's tab set: pass linked-profile locations (Contact) and the
    // image-label flag (Spaces) through, exactly as the legacy nav does.
    const items = getSubRoutes(
        professional,
        canEditProducts,
        layoutData?.linkedProfiles?.linkedProfilesList,
        imageLabelSearchEnabled,
    );
    const navItems = buildProfessionalNavItems(items, {
        hasDownloads: professional.HasDownloads,
    });

    const tabLinks = navItems.map((item) =>
        `${base}${item.link}`.replace(/\/+$/, ''),
    );
    const activeTabLink = getActiveTabLink(tabLinks, currentPath);

    return (
        <>
            {/* Under the logo, which hangs 40 below the banner; 32 more
                between its ring and the pills. Outside the bar, so the bar
                carries none of it once stuck. */}
            <div aria-hidden="true" className="h-[4rem] bg-ground" />
            {/* Stuck under the site header on the directory pill bar's
                pixels: the same offset, layer and band padding, so the
                two bars pin identically. It travels the page's box (the
                shell's), and lets go where that ends before the footer. */}
            <nav
                ref={navRef}
                className="sticky top-[4.75rem] z-[110] bg-ground pt-2 pb-2.5"
            >
                {/* overflow-y-clip is load-bearing: overflow-x-auto alone coerces
                the y axis from visible to auto, making the bar a vertical
                scroll container that any sub-pixel overflow lets mobile wiggle
                and bounce. clip clips without ever scrolling. */}
                <div className="mx-auto flex w-full max-w-(--ap-page-width) [scrollbar-width:none] items-center gap-3 overflow-x-auto overflow-y-clip px-4 md:px-[2.625rem] [&::-webkit-scrollbar]:hidden">
                    {navItems.map((item) => {
                        const to = `${base}${item.link}`.replace(/\/+$/, '');
                        const isActive = to === activeTabLink;
                        // The directory pill bar's pill: the active tab filled
                        // in the page ink, the rest white on the grey ground.
                        const tabClassName = cn(
                            'flex h-[2.75rem] shrink-0 items-center justify-center rounded-[1rem] px-[1.3125rem] font-sans text-base whitespace-nowrap transition-colors max-md:min-h-[44px]',
                            isActive
                                ? 'bg-[#323232] text-white'
                                : 'bg-white text-[#323232] hover:bg-[#f2f2f2]',
                        );
                        if (item.opensEnquiry) {
                            return (
                                <ProfessionalContactTabButton
                                    key={item.link}
                                    className={tabClassName}
                                />
                            );
                        }
                        return (
                            <RoccoLink
                                key={item.link}
                                to={to}
                                // "intent", not "render" — see the desktop pill
                                // nav: nine rendered prefetches each re-run the
                                // overview-merged tab loader.
                                prefetch="intent"
                                // The router's top reset stands down: the effect
                                // above aligns the nav to the viewport top on tab
                                // change, keeping the reader at the tabs.
                                preventScrollReset
                                className={tabClassName}
                            >
                                {LABEL_OVERRIDES[item.label] ?? item.label}
                            </RoccoLink>
                        );
                    })}
                </div>
                {/* The pill bar's rule under its pills. */}
                <div className="px-4 pt-4 md:px-[2.625rem]">
                    <hr className="m-0 h-0 border-t border-[#E9E9E9]" />
                </div>
            </nav>
        </>
    );
};
